import axios from "axios";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { refreshTokenApi } from "@/features/auth/api/auth.api";

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

let isRefreshing = false;
let lastRefreshTime = 0;
const REFRESH_COOLDOWN_MS = 30_000; // 30 seconds between refresh attempts

let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

instance.interceptors.request.use(async (config) => {
  const { token } = useAuthStore.getState();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // Cooldown: if we refreshed very recently, use the current token
      const timeSinceLastRefresh = Date.now() - lastRefreshTime;
      if (lastRefreshTime > 0 && timeSinceLastRefresh < REFRESH_COOLDOWN_MS) {
        console.log(
          `[AXIOS-INTERCEPTOR] 401 received — using recent token (refreshed ${Math.round(timeSinceLastRefresh / 1000)}s ago)`,
        );
        const { token: currentToken } = useAuthStore.getState();
        if (currentToken) {
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${currentToken}`;
          return instance(originalRequest);
        }
      }

      console.log(
        "[AXIOS-INTERCEPTOR] 401 received — starting token refresh...",
      );
      originalRequest._retry = true;
      isRefreshing = true;

      const { refresh_token, logout, login, user } = useAuthStore.getState();

      if (!refresh_token) {
        processQueue(new Error("No refresh token"), null);
        logout();
        return Promise.reject(error);
      }

      try {
        const response = await refreshTokenApi(refresh_token);
        const newToken = response.access_token || response.token;
        const newRefreshToken = response.refresh_token || refresh_token;

        login({
          user: user!,
          token: newToken,
          refresh_token: newRefreshToken,
        });

        lastRefreshTime = Date.now();
        console.log("[AXIOS-INTERCEPTOR] ✅ Token refreshed successfully");

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        return instance(originalRequest);
      } catch (refreshError) {
        console.log(`[AXIOS-INTERCEPTOR] ❌ Refresh failed:`, refreshError);
        processQueue(refreshError as Error, null);
        logout();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default instance;
